// Strict ISO date helpers (YYYY-MM-DD) for local-time math without UTC drift.
// Use these instead of `new Date(str)` when the input is a backend date string —
// the global Date constructor accepts garbage silently and returns Invalid Date,
// which propagates through comparisons as `false` everywhere.

// pt-BR month labels, indexed 0-11. Lowercase form for prose ('5 de mar de 2026'),
// capitalized short form for axis/header labels ('Mar').
export const MONTH_LABELS_PT = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez',
];

export const MONTH_LABELS_PT_SHORT = MONTH_LABELS_PT.map(
  (m) => m.charAt(0).toUpperCase() + m.slice(1),
);

export interface IsoDateParts {
  y: number;
  m: number; // 1-12
  d: number; // 1-31
}

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parseIsoDateParts(str: string): IsoDateParts | null {
  const match = ISO_DATE_RE.exec(str);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m, d };
}

// Parses a YYYY-MM-DD string as local midnight. Returns null on malformed input.
export function parseIsoDate(str: string): Date | null {
  const parts = parseIsoDateParts(str);
  if (!parts) return null;
  const date = new Date(parts.y, parts.m - 1, parts.d);
  date.setHours(0, 0, 0, 0);
  return date;
}

// Formats a YYYY-MM-DD string as pt-BR dd/mm/yyyy. Returns '' on malformed input.
export function formatBrDate(str: string): string {
  const parts = parseIsoDateParts(str);
  if (!parts) return '';
  return `${String(parts.d).padStart(2, '0')}/${String(parts.m).padStart(2, '0')}/${parts.y}`;
}

// Date + time in pt-BR (dd/mm/aaaa hh:mm). Accepts ISO timestamps and
// "YYYY-MM-DD HH:MM:SS" (treated as local wall-clock time, since such
// timestamps carry no timezone); date-only values fall back to the date
// alone, null to '—', unparseable input to the raw string.
export function formatBRDateTime(value: string | null): string {
  if (!value) return '—';
  if (ISO_DATE_RE.test(value)) return formatBrDate(value);
  const date = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// Inverse of parseIsoDate — formats a Date as YYYY-MM-DD using local components.
export function formatIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Formats a date range in pt-BR, collapsing month/year when shared.
//   formatWeekRange('2026-03-09', '2026-03-15') → '9 a 15 de mar de 2026'
//   formatWeekRange('2026-03-30', '2026-04-05') → '30 de mar a 5 de abr de 2026'
//   formatWeekRange('2025-12-29', '2026-01-04') → '29 de dez de 2025 a 4 de jan de 2026'
export function formatWeekRange(startStr: string, endStr: string): string {
  const start = parseIsoDateParts(startStr);
  const end = parseIsoDateParts(endStr);
  if (!start || !end) return `${startStr} a ${endStr}`;
  const sMonth = MONTH_LABELS_PT[start.m - 1];
  const eMonth = MONTH_LABELS_PT[end.m - 1];
  if (start.y === end.y && start.m === end.m) return `${start.d} a ${end.d} de ${sMonth} de ${start.y}`;
  if (start.y === end.y) return `${start.d} de ${sMonth} a ${end.d} de ${eMonth} de ${start.y}`;
  return `${start.d} de ${sMonth} de ${start.y} a ${end.d} de ${eMonth} de ${end.y}`;
}
